/**
 * The media a carousel slide shows on the card.
 *
 * Since the original-level media pool (2026-10-10) every slide of a post
 * reads the SAME list — the article's pool, which grows whenever any slide
 * adds a photo — and a slide's own pick is a URL on the slide
 * (`media_url`, mirrored in `primary_image_url`). So the position of the
 * pick is derived from the URL on every hydration and never remembered:
 * a remembered position pointed at a neighbour's photo once the shared
 * list had grown under it (tend.ooo item 5256, 2026-10-12).
 */

export interface SlideMediaLike {
  media_urls?: string[] | null;
  media_url?: string | null;
  primary_image_url?: string | null;
}

export interface SlideMediaState {
  /** The list the strip and the Media Control walk. */
  pool: string[];
  /** The slide's pick within `pool`. */
  index: number;
}

/** The slide's pick: the saved URL first, the legacy mirror second. */
export const slidePickUrl = (slide: SlideMediaLike): string =>
  slide.media_url || slide.primary_image_url || '';

/**
 * A photo added on one slide belongs to the whole post (the pool is the
 * article's): every slide's list gains it at once, so the Media counter and
 * the gallery on the other slides agree with what the server will read
 * back, without a reload. Slides that already list it are returned as-is.
 */
export function withPhotoOnEverySlide<T extends { media_urls?: string[] | null }>(slides: T[], url: string): T[] {
  if (!url) return slides;
  return slides.map((s) => {
    const list = s.media_urls || [];
    return list.includes(url) ? s : { ...s, media_urls: [...list, url] };
  });
}

/**
 * A per-slide save lands on the card's local copy of that slide too — the
 * slide-switch hydration reads the local list, so a save that only went to
 * the server reverted on the way back (fit position, 2026-10-12). Other
 * slides are returned as the same objects; an unknown id leaves the array.
 */
export function patchSlide<T extends { id: number }>(slides: T[], slideId: number, patch: Partial<T>): T[] {
  if (!slides.some((s) => s.id === slideId)) return slides;
  return slides.map((s) => (s.id === slideId ? { ...s, ...patch } : s));
}

/**
 * Which axis `object-fit: cover` crops for a media of natural size `dims`
 * in a slot of aspect `slotRatioStr` ("4/5"): 'x' when the media is wider
 * than the slot, 'y' when taller, null when they match. Unknown dims read
 * as 'y' (the template editor, before the first load). The Fill slider
 * drives this axis, so it must come from the media on screen.
 */
export function croppedAxisFor(dims: { w: number; h: number } | null | undefined,
                               slotRatioStr: string | null | undefined): 'x' | 'y' | null {
  if (!dims || !dims.w || !dims.h) return 'y';
  if (!slotRatioStr) return null;
  const [sw, sh] = slotRatioStr.split('/').map(Number);
  if (!sw || !sh) return null;
  const slotRatio = sw / sh;
  const mediaRatio = dims.w / dims.h;
  if (Math.abs(mediaRatio - slotRatio) < 0.01) return null;
  return mediaRatio > slotRatio ? 'x' : 'y';
}

export function resolveSlideMedia(slide: SlideMediaLike): SlideMediaState {
  const pick = slidePickUrl(slide);
  const listed = (slide.media_urls || []).filter((u): u is string => !!u);
  const pool = listed.length > 0 ? [...listed] : (pick ? [pick] : []);
  let index = pick ? pool.indexOf(pick) : -1;
  if (index < 0 && pick) {
    // The server keeps a slide's selection in its list even when excluded;
    // mirror that here so a pick that is not (yet) in the list still shows.
    pool.push(pick);
    index = pool.length - 1;
  }
  return { pool, index: index < 0 ? 0 : index };
}
